import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Menu, X, Search, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from './ui/sheet';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const { cartCount, setIsCartOpen } = useCart();
  const { isAuthenticated, user, login, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (isAuthenticated) {
        try {
          await axios.get(`${BACKEND_URL}/api/admin/categories`, { withCredentials: true });
          setIsAdmin(true);
        } catch (error) {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [isAuthenticated]);

  const navLinks = [
    { name: 'Shop', href: '/shop' },
    { name: 'New Arrivals', href: '/shop?filter=new' },
    { name: 'Sale', href: '/shop?filter=sale' },
    { name: 'About', href: '/about' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-coconut/95 backdrop-blur-sm border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <span className="font-display text-2xl font-bold text-text-primary tracking-tight">
              The Fashion House
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-text-subtle hover:text-text-primary transition-colors duration-200 text-sm font-medium tracking-wide"
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="hidden md:flex text-text-subtle hover:text-text-primary">
              <Search className="h-5 w-5" />
            </Button>

            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-3">
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="ghost" size="sm" className="text-accent-ui hover:text-accent-ui/80">
                      <Settings className="h-4 w-4 mr-1" />
                      Admin
                    </Button>
                  </Link>
                )}
                <span className="text-sm text-text-subtle">{user?.name?.split(' ')[0]}</span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={logout}
                  className="text-text-subtle hover:text-text-primary"
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={login}
                className="hidden md:flex text-text-subtle hover:text-text-primary"
              >
                <User className="h-5 w-5" />
              </Button>
            )}

            <Button 
              variant="ghost" 
              size="icon" 
              className="relative text-text-subtle hover:text-text-primary"
              onClick={() => setIsCartOpen(true)}
            >
              <ShoppingBag className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-accent-ui text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {cartCount}
                </span>
              )}
            </Button>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-text-subtle">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-coconut">
                <SheetHeader>
                  <SheetTitle className="font-display text-xl text-text-primary">Menu</SheetTitle>
                </SheetHeader>
                <nav className="mt-8 flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-text-primary hover:text-accent-ui transition-colors py-2 text-lg font-medium"
                    >
                      {link.name}
                    </Link>
                  ))}
                  <div className="border-t border-stone-200 pt-4 mt-4">
                    {isAuthenticated ? (
                      <>
                        <p className="text-text-subtle text-sm mb-2">Signed in as {user?.email}</p>
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                            <Button className="w-full mb-2 bg-accent-ui hover:bg-accent-ui/90">
                              <Settings className="h-4 w-4 mr-2" />
                              Admin Dashboard
                            </Button>
                          </Link>
                        )}
                        <Button onClick={() => { logout(); setMobileMenuOpen(false); }} variant="outline" className="w-full">
                          Sign Out
                        </Button>
                      </>
                    ) : (
                      <Button onClick={() => { login(); setMobileMenuOpen(false); }} className="w-full bg-accent-ui hover:bg-accent-ui/90">
                        Sign In with Google
                      </Button>
                    )}
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
